"use client";

import React, { useCallback, useEffect, useState, useRef } from "react";
import ManabakhLogo from "@/helpers/svg/manabakhLogo.svg";
import { useRouter, useSearchParams } from "next/navigation";
import LoginLogoResponsive from "@/helpers/svg/loginLogoResponsive.svg";
import PN from "persian-number";
import SlideAnimationFrame from "@/components/elements/slideAnimationFrame";
import {
  AddProfile,
  AgeRatingSelectionView,
  LoginView,
  OTPView,
  PasswordView,
  SelectProfileView,
  UserLimitView,
} from "./views";
import { enqueueSnackbar } from "notistack";
import Link from "next/link";
import { useAuth } from "@/providers/authProvider";

// Auth V2 service imports
import {
  generateLoginToken,
  sendLoginOTP,
  loginRegister,
  selectProfile,
} from "@/services/authService";
import { getUser, addProfile as addProfileService, deleteProfile } from "@/services/userService";
import {
  deleteSession,
  getSessions,
  isWithinSessionLimit,
} from "@/services/sessionService";
import { getPublicAuthConfigSync } from "@/services/publicAuthConfigService";
import { getErrorMessage } from "@/lib/errorHandler";
import { trackGtagEvent } from "@/services/gtagAnalyticsService";

/**
 * Login Stage Map:
 *  0 = LoginView          (phone input)
 *  1 = OTPView            (OTP verification)
 *  2 = AgeRatingView      (age rating selection)
 *  3 = SelectProfileView  (profile selection)
 *  4 = AddProfile         (create new profile)
 *  5 = UserLimitView      (session management)
 *  6 = PasswordView       (password input)
 */

const Login = () => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [otpTimer, setOtpTimer] = useState(0);
  const [loginStage, setLoginStage] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [loginErr, setLoginErr] = useState(false);
  const [otpErr, setOtpErr] = useState(false);
  const [otpResetKey, setOtpResetKey] = useState(0);
  const [loginLoading, setLoginLoading] = useState(false);
  const [addProfileLoading, setAddProfileLoading] = useState(false);
  const [selectProfileLoading, setSelectProfileLoading] = useState(false);
  const [userListLoading, setUserListLoading] = useState(false);
  const [userList, setUserList] = useState([]);
  const [deleteProfileLoading, setDeleteProfileLoading] = useState(null); // holds profileId being deleted
  const router = useRouter();
  const searchparams = useSearchParams();
  const { login: authLogin } = useAuth();
  const returnPath = searchparams.get("ref");
  const safeReturnPath =
    returnPath?.startsWith("/") && !returnPath.startsWith("//")
      ? returnPath
      : null;

  // Login attempt token — binds the entire auth flow to this session
  const loginAttemptToken = useRef(null);
  const loginAttemptExpiresAt = useRef(0);
  // Store temporary tokens between OTP verification and profile selection
  const tempTokens = useRef({ accessToken: null, refreshToken: null });
  const authMethod = useRef("otp");

  // Session management state
  const [sessionList, setSessionList] = useState([]);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null); // holds sessionId being deleted

  useEffect(() => {
    const timer = setInterval(() => {
      setOtpTimer((prevTime) => {
        if (prevTime === 0) {
          clearInterval(timer);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [otpTimer]);

  const toggleVisibility = () => setIsVisible(!isVisible);

  const handlePhoneNumber = (e) => {
    const regex = /^[0-9]*$/;
    const value = PN.convertPeToEn(e.target.value);
    if (value?.length < 12 && regex.test(value)) {
      setPhoneNumber(value);
    }
  };

  const handlePassword = (e) => {
    setPassword(e.target.value);
  };

  const validatePhoneNumber = useCallback(() => {
    const regex = new RegExp("^(\\+98|0)?9\\d{9}$");
    const isValid = regex.test(phoneNumber) && phoneNumber.length === 11;
    setLoginErr(!isValid);
    return isValid;
  }, [phoneNumber]);

  const getLoginAttemptToken = useCallback(async () => {
    if (
      loginAttemptToken.current &&
      loginAttemptExpiresAt.current > Date.now() + 5000
    ) {
      return loginAttemptToken.current;
    }

    const result = await generateLoginToken();
    loginAttemptToken.current = result.token;
    loginAttemptExpiresAt.current = result.expiresAt;
    return result.token;
  }, []);

  const loadTemporaryUser = useCallback(async (accessToken) => {
    const userData = await getUser({ accessToken });
    setUserList(userData?.profiles || []);
    return userData;
  }, []);

  const finishCredentialStep = useCallback(
    async (result, method) => {
      if (!result?.accessToken) {
        throw new Error("احراز هویت توکن موقت معتبری برنگرداند");
      }

      tempTokens.current = {
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
      };
      authMethod.current = method;
      setOtpTimer(0);
      await loadTemporaryUser(result.accessToken);

      // Use the bearer token returned by login-register to inspect the valid
      // /sessions endpoint before profile selection so an over-limit user can
      // remove a session and continue the same login attempt.
      try {
        const sessions = await getSessions({
          accessToken: result.accessToken,
        });
        const { maxSessionCount } = getPublicAuthConfigSync();
        setSessionList(sessions);

        if (!isWithinSessionLimit(sessions, maxSessionCount)) {
          setLoginStage(5);
          return;
        }
      } catch {
        // Do not block normal profile selection when the optional preflight
        // session check is temporarily unavailable.
      }

      setLoginStage(3);
    },
    [loadTemporaryUser]
  );

  // -----------------------------------------------------------------------
  // Session management helpers
  // -----------------------------------------------------------------------
  async function fetchSessions() {
    setSessionLoading(true);
    try {
      const list = await getSessions({
        accessToken: tempTokens.current.accessToken,
      });
      setSessionList(list);
      return list;
    } catch (err) {
      enqueueSnackbar(
        getErrorMessage(err) || "خطا در دریافت لیست نشست‌ها",
        { variant: "error" }
      );
      return null;
    } finally {
      setSessionLoading(false);
    }
  }

  async function handleDeleteSession(sessionId) {
    setDeleteLoading(sessionId);
    try {
      await deleteSession(sessionId, {
        accessToken: tempTokens.current.accessToken,
      });

      const list = await fetchSessions();

      enqueueSnackbar("دستگاه با موفقیت خارج شد.", { variant: "success" });
      const { maxSessionCount } = getPublicAuthConfigSync();
      if (isWithinSessionLimit(list, maxSessionCount)) {
        setLoginStage(3);
      }
    } catch (err) {
      enqueueSnackbar(
        getErrorMessage(err) || "خطا در خروج دستگاه",
        { variant: "error" }
      );
    } finally {
      setDeleteLoading(null);
    }
  }

  // -----------------------------------------------------------------------
  // Delete profile (during login flow, using temp tokens)
  // -----------------------------------------------------------------------
  async function handleDeleteProfile(profileId) {
    setDeleteProfileLoading(profileId);
    try {
      await deleteProfile(profileId, {
        accessToken: tempTokens.current.accessToken,
      });
      // Re-fetch profiles
      await loadTemporaryUser(tempTokens.current.accessToken);
      enqueueSnackbar("پروفایل با موفقیت حذف شد.", { variant: "success" });
    } catch (err) {
      enqueueSnackbar(
        getErrorMessage(err) || "خطا در حذف پروفایل",
        { variant: "error" }
      );
    } finally {
      setDeleteProfileLoading(null);
    }
  }

  // -----------------------------------------------------------------------
  // Add profile
  // -----------------------------------------------------------------------
  const handleAddUser = async (fullName, ageRange, imageFile) => {
    setAddProfileLoading(true);
    try {
      await addProfileService(
        { fullName, ageRange, image: imageFile || undefined },
        { accessToken: tempTokens.current.accessToken }
      );
      trackGtagEvent("profile_created", {
        source: "login_flow",
        age_range: ageRange,
        has_image: Boolean(imageFile),
      });
      await loadTemporaryUser(tempTokens.current.accessToken);
      // After adding profile, go back to select-profile stage
      setLoginStage(3);
    } catch (error) {
      enqueueSnackbar(
        getErrorMessage(error) || "خطای ناشناخته در حین ساخت پروفایل"
      );
    }
    setAddProfileLoading(false);
  };

  const goToAddView = async () => setLoginStage(4);

  // -----------------------------------------------------------------------
  // Login (send OTP)
  // -----------------------------------------------------------------------
  const handleLogin = async (retry = false) => {
    if (loginLoading) return;

    if (validatePhoneNumber()) {
        setLoginLoading(true);
        try {
          const token = await getLoginAttemptToken();

          await sendLoginOTP({
            mobileNumber: Number(phoneNumber),
            loginAttemptToken: token,
          });

          if (retry)
            enqueueSnackbar(`کد تائید برای شما ارسال شد`, {
              variant: "success",
            });
          setOtpTimer(120);
          setLoginStage(1);
          setLoginErr(false);
          setOtpErr(false);
          setOtpResetKey((k) => k + 1);
          setLoginLoading(false);
        } catch (err) {
          if (
            err?.response?.status === 429 ||
            err?.response?.status === 422
          ) {
            enqueueSnackbar(
              `تعداد درخواست بیشتر از بازه مجاز می باشد لطفا کمی بعد تلاش کنید`,
              { variant: "warning" }
            );
          } else {
            enqueueSnackbar(
              getErrorMessage(err) ||
              `خطا در ارسال کد تائید لطفا کمی بعد مجددا تلاش کنید`,
              { variant: "warning" }
            );
          }
          setLoginLoading(false);
        }
    }
  };

  // -----------------------------------------------------------------------
  // OTP verification → check sessions → decide stage
  // -----------------------------------------------------------------------
  async function handleOtp(otpInput) {
    try {
      setOtpErr(false);

      // Step 3: Login / Register with login attempt token
      const result = await loginRegister({
        mobileNumber: Number(phoneNumber),
        otp: Number(otpInput),
        loginAttemptToken: loginAttemptToken.current || undefined,
      });

      await finishCredentialStep(result, "otp");
    } catch (err) {
      if (err.response?.status === 400 || err.response?.status === 403) {
        setOtpErr(true);
      } else {
        enqueueSnackbar(
          getErrorMessage(err) || "خطا در تأیید کد، لطفا مجددا تلاش کنید",
          { variant: "error" }
        );
        setOtpErr(true);
      }
    }
  }

  async function handlePasswordLogin() {
    if (loginLoading || !validatePhoneNumber()) return;
    if (!password) {
      enqueueSnackbar("رمز عبور را وارد کنید", { variant: "warning" });
      return;
    }

    setLoginLoading(true);
    try {
      const token = await getLoginAttemptToken();
      const result = await loginRegister({
        mobileNumber: Number(phoneNumber),
        password,
        loginAttemptToken: token,
      });
      await finishCredentialStep(result, "password");
    } catch (err) {
      enqueueSnackbar(
        getErrorMessage(err) || "شماره موبایل یا رمز عبور نامعتبر است",
        { variant: "error" }
      );
    } finally {
      setLoginLoading(false);
    }
  }

  // -----------------------------------------------------------------------
  // Select profile
  // -----------------------------------------------------------------------
  async function handleSelectProfile(id) {
    setSelectProfileLoading(true);
    try {
      // Step 4: Select profile with login attempt token
      const result = await selectProfile(
        {
          profileId: id,
          loginAttemptToken: loginAttemptToken.current || undefined,
        },
        tempTokens.current.accessToken
      );

      // Use the auth provider to handle login with FINAL tokens
      const selectedProfile =
        result.profile || userList.find((profile) => profile._id === id) || null;
      await authLogin(
        result.accessToken,
        result.refreshToken,
        selectedProfile
      );
      trackGtagEvent("login_success", {
        method: authMethod.current,
        source: "login_flow",
        has_referrer: Boolean(safeReturnPath),
      });
      trackGtagEvent("profile_selected", {
        source: "login_flow",
        profile_id: id,
      });

      // Clean up temp tokens
      loginAttemptToken.current = null;
      loginAttemptExpiresAt.current = 0;
      tempTokens.current = { accessToken: null, refreshToken: null };

      enqueueSnackbar(`به منه باخ خوش آمدید!`, {
        variant: "success",
      });
      if (safeReturnPath) {
        router.replace(safeReturnPath);
      } else {
        router.push("/");
      }
    } catch (err) {
      const errorMessage = String(err.response?.data?.message || "");
      const isSessionLimitError =
        [400, 409].includes(err.response?.status) &&
        /session limit|maximum session/i.test(errorMessage);

      if (isSessionLimitError) {
        enqueueSnackbar(
          `تعداد نشست‌های فعال شما به حداکثر رسیده است. لطفا یکی را حذف کنید.`,
          { variant: "warning" }
        );
        // Fetch sessions and show management view
        const sessions = await fetchSessions();
        if (!sessions.length) {
          enqueueSnackbar(
            "نشستی برای حذف پیدا نشد. لطفا دوباره تلاش کنید.",
            { variant: "error" }
          );
        } else {
          setLoginStage(5);
        }
      } else {
        enqueueSnackbar(
          getErrorMessage(err) || `خطا در انتخاب پروفایل`,
          { variant: "error" }
        );
      }
    } finally {
      setSelectProfileLoading(false);
    }
  }

  // -----------------------------------------------------------------------
  // Fetch user profiles when entering stage 3
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (loginStage === 3) {
      async function getData() {
        setUserListLoading(true);
        try {
          await loadTemporaryUser(tempTokens.current.accessToken);
        } catch (err) {
          enqueueSnackbar(
            getErrorMessage(err) || "خطا در دریافت پروفایل‌ها",
            { variant: "error" }
          );
        } finally {
          setUserListLoading(false);
        }
      }
      getData();
    }
  }, [loginStage, loadTemporaryUser]);

  async function handleAgeRating() {
    try {
      const userData = await getUser({
        accessToken: tempTokens.current.accessToken,
      });
      return userData;
    } catch (err) {
      return err;
    }
  }

  function handleKeyPress(e) {
    if (e.key === "Enter") {
      handleLogin();
    }
  }

  // -----------------------------------------------------------------------
  // Props for each view
  // -----------------------------------------------------------------------
  const loginViewProps = {
    phoneNumber,
    handlePhoneNumber,
    handleKeyPress,
    handleLogin,
    goToPassword: () => {
      if (validatePhoneNumber()) setLoginStage(6);
    },
    loginLoading,
    loginErr,
  };

  const otpViewProps = {
    phoneNumber,
    otpErr,
    handleOtp,
    otpTimer,
    setLoginStage,
    handleLogin,
    loginStage,
    loginLoading,
    otpResetKey,
  };

  const addProfileProps = {
    username,
    setUsername,
    handleAddUser,
    addProfileLoading,
  };

  const selectProfileProps = {
    userListLoading,
    userList,
    selectProfileLoading,
    handleSelectProfile,
    goToAddView,
    onDeleteProfile: handleDeleteProfile,
    deleteProfileLoading,
  };

  const passwordViewProps = {
    password,
    handlePassword,
    isVisible,
    toggleVisibility,
    handlePasswordLogin,
    loginLoading,
    onBack: () => setLoginStage(0),
  };

  const userLimitProps = {
    sessions: sessionList,
    sessionLoading,
    onDeleteSession: handleDeleteSession,
    deleteLoading,
  };

  return (
    <div className="h-[100vh]">
      <div className="h-full px-0 ssm:px-3 md:px-10 flex flex-col items-center">
        <div className="flex flex-col justify-center items-center mt-10">
          <div className="hidden lg:flex font-bold text-inherit px-8 pb-6 mb-6 border-b-2 border-solid border-[#3F0303]">
            <Link href="/">
              <ManabakhLogo />
            </Link>
          </div>
          <div className="flex lg:hidden font-bold text-inherit px-8 pb-4 mb-2 border-b-2 mt-5 border-solid border-[#3F0303]">
            <Link href="/">
              <LoginLogoResponsive />
            </Link>
          </div>
          <h3 className="text-primary-white text-base lg:text-4xl font-semibold">
            ورود
          </h3>
        </div>
        <div className="flex justify-center items-center mb-12 gr-border !bg-[#0F0B0B] mx-4 w-full max-w-[635px] lg:max-w-[768px] h-auto  min-h-[400px] mt-5">
          <SlideAnimationFrame
            instanceId="login-handler"
            activeSectionIndex={loginStage}
            noAnimatedContainer
            sections={[
              {
                id: "login-form-view",
                children: <LoginView {...loginViewProps} />,
              },
              {
                id: "otp-view",
                children: <OTPView {...otpViewProps} />,
              },
              {
                id: "age-rating-view",
                children: (
                  <AgeRatingSelectionView handleAgeRating={handleAgeRating} />
                ),
              },
              {
                id: "profile-Select-view",
                children: <SelectProfileView {...selectProfileProps} />,
              },
              {
                id: "add-profile-view",
                children: <AddProfile {...addProfileProps} />,
              },
              {
                id: "user-limit-view",
                children: <UserLimitView {...userLimitProps} />,
              },
              {
                id: "password-view",
                children: <PasswordView {...passwordViewProps} />,
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
};

export default Login;
